/**
 * DeepSpaceBackground — Northstar atmosphere.
 *
 * Pure CSS + a lightweight canvas particle field. Deep navy base, a
 * subtle green nebula wash, quiet drifting particles. Restrained
 * orbital ambience — never gaming/cyberpunk.
 *
 * `prefers-reduced-motion: reduce` produces a genuinely calm static
 * scene: particles are drawn once and never animated, and the nebula
 * pulses are disabled. Not a broken shell — a quieter one.
 */
import React, { useEffect, useRef } from "react";

export default function DeepSpaceBackground({ className = "" }) {
  const canvasRef = useRef(null);
  const reducedMotion =
    typeof window !== "undefined" &&
    window.matchMedia("(prefers-reduced-motion: reduce)").matches;

  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext("2d");
    let raf = 0;
    let running = true;

    const dpr = window.devicePixelRatio || 1;
    const resize = () => {
      canvas.width = window.innerWidth * dpr;
      canvas.height = window.innerHeight * dpr;
      canvas.style.width = window.innerWidth + "px";
      canvas.style.height = window.innerHeight + "px";
      ctx.setTransform(1, 0, 0, 1, 0, 0);
      ctx.scale(dpr, dpr);
    };
    resize();
    window.addEventListener("resize", resize);

    // Sparser field = calmer, more premium. Not a game.
    const PARTICLES = reducedMotion ? 60 : 140;
    const stars = Array.from({ length: PARTICLES }, () => ({
      x: Math.random() * window.innerWidth,
      y: Math.random() * window.innerHeight,
      r: Math.random() * 1.2 + 0.2,
      // Very slow drift, subtle enough to feel like breath, not motion.
      vx: (Math.random() - 0.5) * 0.08,
      vy: (Math.random() - 0.5) * 0.05,
      base: 0.3 + Math.random() * 0.6,
      // Two color families: soft white and a whisper of BTC green.
      green: Math.random() < 0.15,
    }));

    const draw = (t) => {
      if (!running) return;
      ctx.clearRect(0, 0, window.innerWidth, window.innerHeight);
      for (const s of stars) {
        if (!reducedMotion) {
          s.x += s.vx;
          s.y += s.vy;
          if (s.x < 0) s.x = window.innerWidth;
          if (s.x > window.innerWidth) s.x = 0;
          if (s.y < 0) s.y = window.innerHeight;
          if (s.y > window.innerHeight) s.y = 0;
        }
        const twinkle = reducedMotion
          ? s.base
          : s.base + Math.sin(t / 1500 + s.x) * 0.15;
        const alpha = Math.max(0.05, Math.min(1, twinkle));
        ctx.beginPath();
        ctx.arc(s.x, s.y, s.r, 0, Math.PI * 2);
        ctx.fillStyle = s.green
          ? `rgba(126, 212, 147, ${alpha * 0.55})`
          : `rgba(255, 255, 255, ${alpha})`;
        ctx.fill();
      }
      if (reducedMotion) {
        running = false;
        return;
      }
      raf = requestAnimationFrame(draw);
    };
    raf = requestAnimationFrame(draw);

    return () => {
      running = false;
      cancelAnimationFrame(raf);
      window.removeEventListener("resize", resize);
    };
  }, [reducedMotion]);

  return (
    <div
      aria-hidden="true"
      className={`absolute inset-0 z-0 overflow-hidden pointer-events-none ${className}`}
      data-testid="northstar-atmosphere"
      data-reduced-motion={reducedMotion ? "true" : "false"}
    >
      {/* Base gradient wash */}
      <div className="absolute inset-0 bg-[radial-gradient(ellipse_at_center,#0a1c3a_0%,#040816_65%,#02040a_100%)]" />

      {/* Whisper of BTC green — the nebula. Pulses gently unless
          reduced motion is on, in which case it's simply static. */}
      <div
        className={`absolute -top-1/3 left-1/2 -translate-x-1/2 w-[120vw] h-[120vh] rounded-full opacity-30 blur-3xl
          bg-[radial-gradient(circle,#2D9B4E_0%,transparent_55%)]
          ${reducedMotion ? "" : "animate-[ns-pulse_9s_ease-in-out_infinite]"}`}
      />
      <div
        className={`absolute bottom-[-30vh] right-[-10vw] w-[70vw] h-[70vh] rounded-full opacity-20 blur-3xl
          bg-[radial-gradient(circle,#1F5D5B_0%,transparent_60%)]
          ${reducedMotion ? "" : "animate-[ns-pulse_13s_ease-in-out_infinite]"}`}
      />

      <canvas ref={canvasRef} className="absolute inset-0" />

      {/* Grain overlay for depth. Never animated. */}
      <div
        className="absolute inset-0 opacity-[0.035] pointer-events-none mix-blend-overlay"
        style={{
          backgroundImage:
            "url(\"data:image/svg+xml;utf8,<svg xmlns='http://www.w3.org/2000/svg' width='120' height='120'><filter id='n'><feTurbulence type='fractalNoise' baseFrequency='0.9' numOctaves='2' stitchTiles='stitch'/></filter><rect width='100%' height='100%' filter='url(%23n)' opacity='0.7'/></svg>\")",
        }}
      />

      <style>{`
        @keyframes ns-pulse {
          0%, 100% { opacity: 0.25; transform: translate(-50%, 0) scale(1); }
          50%      { opacity: 0.42; transform: translate(-50%, 0) scale(1.06); }
        }
      `}</style>
    </div>
  );
}
